/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { animate, style, transition, trigger } from "@angular/animations";
import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	Component,
	type Signal,
	TemplateRef,
	ViewEncapsulation,
	computed,
	contentChild,
	inject,
	input,
	output,
	signal,
} from "@angular/core";
import { Router, RouterModule } from "@angular/router";
import { CbRoutesUtils } from "@app/@classes/routes/cb-routes-utils";
import { RealtimeRoutePresenceService } from "@app/@services/generic/realtime-route-presence.service";
import { AuthTenantService } from "@app/@services/shared/auth-tenant.service";
import { BugReportDialogComponent } from "@app/modules/common/dialogs/bug-report-dialog/bug-report-dialog.component";
import { TranslateModule } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { environment } from "src/environments/environment";
import { DialogService } from "../dialog/dialog.service";
import { PopoverModule } from "../floating/popover/popover.module";
import { IconComponent } from "../icon/icon.component";
import { UserAvatarComponent } from "../user-avatar/user-avatar.component";
import { UsersAvatarListComponent } from "../users-avatar-list/users-avatar-list.component";
import { NavItemComponent } from "./nav-item/nav-item.component";

export type CbSidebarGroups = {
	id: string;
	label: string;
};

export type CbSidebarItem = {
	label?: string;
	icon?: string;
	counter?: number;
	groupId?: string;
	showSignal?: Signal<boolean>;
	show?: () => boolean;
} & (
		| {
			type: "route";
			routerLink: string;
		}
		| {
			type: "callback";
			callback: () => void;
		}
		| {
			type: "collapsible";
			isOpen?: boolean;
			items: CbSidebarItem[];
		}
		| {
			type: "subview";
			routerLink: string;
			show?: boolean;
			items: Exclude<CbSidebarItem, { type: "subview" }>[];
		}
	);
@Component({
	selector: "cb-sidebar",
	imports: [
		CommonModule,
		IconComponent,
		NavItemComponent,
		PopoverModule,
		TranslateModule,
		RouterModule,
		UsersAvatarListComponent,
		UserAvatarComponent,
	],
	templateUrl: "./sidebar.component.html",
	styleUrl: "./sidebar.component.scss",
	animations: [
		trigger("subsidebar", [
			transition(":enter", [
				style({ transform: "translateX(-100%)" }),
				animate("120ms ease-out", style({ transform: "translateX(0)" })),
			]),
			transition(":leave", [
				animate("0ms ease-out", style({ transform: "translateX(-100%)" })),
			]),
		]),
	],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent {
	public readonly homeRoute = CbRoutesUtils.paths.choose_office.path;

	private readonly router: Router = inject(Router);
	private readonly tenantService: AuthTenantService = inject(AuthTenantService);
	private readonly routePresenceService: RealtimeRoutePresenceService = inject(
		RealtimeRoutePresenceService,
	);
	private readonly dialogService: DialogService = inject(DialogService);

	public readonly changeTenantUrl = CbRoutesUtils.paths.tenant.path;
	public readonly createTenantUrl = CbRoutesUtils.paths.tenant.create.path;
	public readonly userSettingsUrl = CbRoutesUtils.paths.w.settings.user.path;

	public readonly position = input<"left" | "right">("left");
	public readonly compact = input<boolean>(false);
	public readonly logo = input<string>();
	public readonly navItems = input<CbSidebarItem[]>([]);
	public readonly footerItems = input<CbSidebarItem[]>();

	public readonly navGroups = input<CbSidebarGroups[]>([]);

	public readonly restrictedUserOptions = input<boolean>(false);

	public readonly userId = input<string | null>();
	public readonly profileName = input<string | null>();
	public readonly profileEmail = input<string | null>();

	public readonly tenantName = input<string | null>();

	public readonly sidebarCollapse = input(false);

	public readonly theme = input<"light" | "dark" | "brand" | "white">("dark");
	public readonly size = input<"sm" | "md">("md");

	public readonly logout = output<void>();

	readonly header = contentChild("header", { read: TemplateRef });
	readonly footer = contentChild("footer", { read: TemplateRef });

	public readonly groupedSidebarItems = computed(() => {
		const groups: Record<string, CbSidebarItem[]> = this.navGroups().reduce(
			(acc, g) => {
				acc[g.id] = [];
				return acc;
			},
			{} as Record<string, CbSidebarItem[]>,
		);

		for (const item of this.navItems()) {
			if (item.groupId) {
				groups[item.groupId].push(item);
			}
		}

		return Object.entries(groups).map(([groupId, items]) => {
			const group = this.navGroups().find((g) => g.id === groupId);
			if (!group) {
				throw new Error(`Group with id ${groupId} not found`);
			}
			return {
				group,
				items,
			};
		});
	});

	public readonly ungroupedSidebarItems = computed(() => {
		return this.navItems().filter((item) => !item.groupId);
	});

	public isOpen = signal<boolean>(false);

	//private _currentSubSidebarItems: CbSidebarItem[] = [];

	public currentSubSidebarItems = signal<CbSidebarItem[]>([]);

	public usersOnSameRoute = signal<string[]>([]);

	private destroy$ = new Subject<void>();

	/**
	 * Whether the environment is production.
	 */
	public get isProduction(): boolean {
		return environment.production;
	}

	public routeActivationChanged(item: CbSidebarItem, isActive: boolean): void {
		if (isActive) {
			if (item.type === "subview") {
				this.currentSubSidebarItems.set(item.items);
			} else {
				this.currentSubSidebarItems.set([]);
				this.isOpen.set(false);
			}
		}
	}

	public subrouteClick(item: CbSidebarItem): void {
		this.isOpen.set(false);
	}

	public toggleOpen(): void {
		this.isOpen.set(!this.isOpen);
	}

	public changeTenant(): void {
		this.tenantService.changeTenant();
	}

	public contactSupport(): void {
		// Open the email client with the support email
		// With the subject "Support Request"
		// And the body "Please describe your issue here"
		const header = `[${environment.projectName}] Richiesta di supporto`;
		const body = "Descrivi qui il tuo problema";

		window.open(`mailto:info@colibryx.com?subject=${header}&body=${body}`);
	}

	public reportBug(): void {
		this.dialogService.open(BugReportDialogComponent);
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
